import { I18n } from "@/i18n/I18n";
import { Link, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Collapse } from "react-bootstrap";
import { DownCaret } from "../../SVGs/DownCaret";
import { RightCaret } from "../../SVGs/RightCaret";
import "./ProjectNav.css";
import { FusawarePermission } from "@/frontend/fusawareUser";
import { useGlobalStore } from "@/frontend/store/GlobalStore";
import { useProjectStore } from "@/frontend/store/ProjectStore";
import { ProjectParentType } from "@/backend/models/project";

interface ProjectNavProps {
  projectId: string;
}

export function ProjectNav({ projectId }: ProjectNavProps) {
  const location = useLocation();
  const userClass = useGlobalStore(state => state.userClass);
  const isActive = (path: string) => location.pathname === path;
  const isPartsActive = location.pathname.includes('/parts');
  const [partsOpen, setPartsOpen] = useState(isPartsActive);
  const project = useProjectStore(state => state.projects.find(p => p.id === projectId));

  useEffect(() => {
    setPartsOpen(isPartsActive);
  }, [isPartsActive]);

  const linkClass = (active: boolean) =>
    `project-nav-link${active ? ' project-nav-link-active' : ''}`;

  const canManageTemplates =
    userClass?.fusaHasPermission(FusawarePermission.ACCESS_PROCEDURE_REQUIREMENT_TEST_METHOD);

  const canManageParts =
    userClass?.fusaHasPermission(FusawarePermission.MANAGE_PARTS)
    && project
    && project.parentType !== ProjectParentType.ORGANIZATION;

  return (
    <aside className="project-nav">
      <nav className="project-nav-list">
        <Link
          to={`/projects/${projectId}`}
          className={linkClass(isActive(`/projects/${projectId}`))}
        >
          {I18n.get('projectDashboard')}
        </Link>

        <Link
          to={`/projects/${projectId}/requirements`}
          className={linkClass(isActive(`/projects/${projectId}/requirements`))}
        >
          {I18n.get('allRequirements')}
        </Link>

        <Link
          to={`/projects/${projectId}/milestones`}
          className={linkClass(isActive(`/projects/${projectId}/milestones`))}
        >
          {I18n.get('milestones')}
        </Link>

        <Link
          to={`/projects/${projectId}/sample-phases`}
          className={linkClass(isActive(`/projects/${projectId}/sample-phases`))}
        >
          {I18n.get('samplePhases')}
        </Link>

        {canManageTemplates && (
          <Link
            to={`/projects/${projectId}/templates`}
            className={linkClass(isActive(`/projects/${projectId}/templates`))}
          >
            {I18n.get('templates')}
          </Link>
        )}
        {userClass?.getOrgId() === project?.propelOrgId && project && (
          <Link
            to={`/projects/${projectId}/tasks`}
            className={linkClass(isActive(`/projects/${projectId}/tasks`))}
          >
            {I18n.get('tasks')}
          </Link>
        )}

        {canManageParts && (
          <>
            <button
              type="button"
              className="project-nav-section-header"
              onClick={() => setPartsOpen(!partsOpen)}
              aria-expanded={partsOpen}
            >
              <span data-testid="parts-dropdown">{I18n.get('parts')}</span>
              <span className="caret-icon">
                {partsOpen ? <DownCaret size="sm" /> : <RightCaret size="sm" />}
              </span>
            </button>

            <Collapse in={partsOpen}>
              <div className="project-nav-section-content">
                <Link
                  to={`/projects/${projectId}/parts?tab=part_instance`}
                  className={linkClass(
                    location.search.includes('tab=part_instance') ||
                    (location.pathname === `/projects/${projectId}/parts` && !location.search.includes('tab=parts'))
                  )}
                >
                  {I18n.get('partInstances')}
                </Link>
                <Link
                  to={`/projects/${projectId}/parts?tab=parts`}
                  className={linkClass(location.search.includes('tab=parts'))}
                  data-testid="parts-link"
                >
                  {I18n.get('parts')}
                </Link>
                <Link
                  to={`/projects/${projectId}/parts?tab=template`}
                  className={`${linkClass(location.search.includes('tab=templates'))} project-nav-link-disabled`}
                  aria-disabled={true}
                >
                  {I18n.get('partTemplates')}
                </Link>
              </div>
            </Collapse>
          </>
        )}
      </nav>
    </aside>
  );
}
